import { type Mapper } from '@coris-ts/repository/mapper/mapper'
import { type ComparedValueEntity } from '../../data/entities/media.entity'
import {
  type SectionMetricsEntity,
  type SectionPeriodEntity,
  type SectionsComparisonEntity,
  type SectionsSummaryEntity,
  type SegmentComparisonEntity,
  type SegmentInfoEntity
} from '../../data/entities/sections.entity'
import { type ComparedValue } from '../models/chat'
import {
  type SectionMetrics,
  type SectionPeriod,
  type SectionsComparison,
  type SectionsSummary,
  type SegmentComparison,
  type SegmentInfo
} from '../models/sections'

const mapComparedValue = (from: ComparedValueEntity): ComparedValue => ({
  current: from.current,
  previous: from.previous,
  diff: from.diff
})

const mapPeriod = (from: SectionPeriodEntity): SectionPeriod => ({
  date: from.date,
  visits: from.visits,
  seconds: from.seconds,
  interactions: from.interactions
})

const mapSection = (from: SectionMetricsEntity): SectionMetrics => ({
  section: from.section,
  visits: mapComparedValue(from.visits),
  uniqueVisitors: mapComparedValue(from.unique_visitors),
  seconds: mapComparedValue(from.seconds),
  interactions: mapComparedValue(from.interactions),
  periods: from.periods.map(mapPeriod)
})

const mapSegmentInfo = (from: SegmentInfoEntity): SegmentInfo => ({
  key: from.key,
  label: from.label,
  kind: from.kind
})

const mapSegmentComparison = (from: SegmentComparisonEntity): SegmentComparison => ({
  key: from.key,
  label: from.label,
  kind: from.kind,
  webUsers: from.web_users,
  totals: {
    secondsPerUser: from.totals.seconds_per_user,
    visitsPerUser: from.totals.visits_per_user,
    interactionsPerUser: from.totals.interactions_per_user
  },
  items: from.items.map((item) => ({
    section: item.section,
    secondsPerUser: item.seconds_per_user,
    visitsPerUser: item.visits_per_user,
    interactionsPerUser: item.interactions_per_user
  }))
})

export class SectionsSummaryEntityToSectionsSummaryMapper implements Mapper<SectionsSummaryEntity, SectionsSummary> {
  public map(from: SectionsSummaryEntity): SectionsSummary {
    return {
      segments: from.segments.map(mapSegmentInfo),
      totals: {
        visits: mapComparedValue(from.totals.visits),
        uniqueVisitors: mapComparedValue(from.totals.unique_visitors),
        seconds: mapComparedValue(from.totals.seconds),
        interactions: mapComparedValue(from.totals.interactions)
      },
      periods: from.periods.map(mapPeriod),
      sections: from.sections.map(mapSection)
    }
  }
}

export class SectionsComparisonEntityToSectionsComparisonMapper
  implements Mapper<SectionsComparisonEntity, SectionsComparison>
{
  public map(from: SectionsComparisonEntity): SectionsComparison {
    return {
      segments: from.segments.map(mapSegmentComparison)
    }
  }
}
